import { Controller } from "@hotwired/stimulus"

// Two-step contact form:
// - Closed (default): only the submit button is visible, with a trailing chevron
//   (chevron-down.svg) hinting that it opens the form. Clicking reveals the fields.
// - Open: the fields are shown above the button, the logo and a Cancel link appear
//   next to the button, and the chevron is hidden so the button now submits.
//   Cancel collapses everything back to the closed state.
export default class extends Controller {
  static targets = ["fields", "submit", "logo", "cancel", "actions"]
  static values = { open: { type: Boolean, default: false } }

  // First click opens the form; once open, the click submits normally.
  reveal(event) {
    if (this.openValue) return

    event.preventDefault()
    this.setOpen(true)

    if (this.hasFieldsTarget) {
      // preventScroll: the browser's own focus scroll would jump straight to the first
      // field; we want the whole form scrolled into view instead (see below).
      this.fieldsTarget.querySelector("input, textarea")?.focus({ preventScroll: true })
    }

    this.scrollIntoView()
  }

  // The revealed fields render below the fold, so bring the form into view rather than
  // leaving the user to scroll down themselves. Waits a frame so the newly shown fields
  // are laid out before we measure.
  scrollIntoView() {
    requestAnimationFrame(() => {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

      this.element.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "nearest"
      })
    })
  }

  // Cancel link: collapse back to the initial closed state.
  close(event) {
    event.preventDefault()
    this.setOpen(false)
  }

  setOpen(open) {
    this.openValue = open

    if (this.hasFieldsTarget) this.fieldsTarget.classList.toggle("hidden", !open)
    if (this.hasLogoTarget) this.logoTarget.classList.toggle("hidden", !open)
    if (this.hasCancelTarget) this.cancelTarget.classList.toggle("hidden", !open)

    // Closed: button group sits on the left (original position). Open: pushed right (sm+ only;
    // on small phones the actions stay left-aligned and stacked).
    if (this.hasActionsTarget) this.actionsTarget.classList.toggle("sm:ml-auto", open)

    // The trailing chevron only makes sense while closed (it signals "opens the form").
    if (this.hasSubmitTarget) this.submitTarget.querySelector("svg")?.classList.toggle("hidden", open)
  }
}
